import { Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { useI18n, LOCALES } from '@/i18n';
import { useBranding } from '@/contexts/BrandingContext';
import { BrandMark } from '@/components/BrandLogo';
import CurrencySelector from '@/components/CurrencySelector';
import ThemeToggle from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import {
  Building2, Heart, Menu, X, LogOut, ChevronDown, DoorOpen,
  User, CalendarDays, Plane, Bell,
} from 'lucide-react';

export function LandingNavbar({ mobileMenuOpen, setMobileMenuOpen }) {
  const { user, login, logout, isAuthenticated, isAdmin } = useAuth();
  const { t, locale, setLocale } = useI18n();
  const { branding: brandData } = useBranding();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2">
            <BrandMark className="w-10 h-10" alt={brandData?.site_name || 'Estadía 365'} />
            <span className="font-bold text-xl text-[#264639] dark:text-[#6CC4A1]" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
              {brandData?.site_name || 'Estadía 365'}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <div className="flex items-center gap-1 bg-[#F5F4F0] dark:bg-[#2A2A2A] rounded-full p-1" data-testid="lang-selector">
              {LOCALES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLocale(l.code)}
                  className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${locale === l.code ? 'bg-white dark:bg-[#3A3A3A] shadow-sm text-[#264639] dark:text-[#6CC4A1]' : 'text-[#78716C] dark:text-[#A8A29E] hover:text-[#264639] dark:hover:text-[#6CC4A1]'}`}
                  data-testid={`lang-${l.code}`}
                >
                  {l.flag}
                </button>
              ))}
            </div>
            <CurrencySelector />
            <Link to="/properties" className="text-[#57534E] dark:text-[#A8A29E] hover:text-[#264639] dark:hover:text-[#6CC4A1] transition-colors" data-testid="nav-properties">
              {t('nav.properties')}
            </Link>
            {isAuthenticated ? (
              <div className="relative group">
                <button className="flex items-center gap-2 border border-[#E7E5E4] dark:border-[#444] rounded-full px-3 py-1.5 hover:shadow-md transition-shadow" data-testid="user-menu-btn">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#264639] flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{user?.name?.charAt(0)}</span>
                    </div>
                  )}
                  <ChevronDown className="w-3 h-3 text-[#78716C] dark:text-[#A8A29E]" />
                </button>
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-[#1E1E1E] rounded-xl shadow-xl border border-[#E7E5E4] dark:border-[#333] py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <div className="px-4 py-2 border-b border-[#E7E5E4] dark:border-[#333]">
                    <p className="text-sm font-medium text-[#1C1917] dark:text-[#F5F4F0] truncate">{user?.name}</p>
                    <p className="text-xs text-[#78716C] dark:text-[#8C857E] truncate">{user?.email}</p>
                  </div>
                  {isAdmin ? (
                    <>
                      <Link to="/dashboard" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#57534E] dark:text-[#A8A29E] hover:bg-[#F5F4F0] dark:hover:bg-[#2A2A2A]" data-testid="menu-dashboard">
                        <Building2 className="w-4 h-4" /> {t('nav.dashboard')}
                      </Link>
                      <Link to="/wishlist" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#57534E] dark:text-[#A8A29E] hover:bg-[#F5F4F0] dark:hover:bg-[#2A2A2A]" data-testid="menu-favorites">
                        <Heart className="w-4 h-4" /> {t('nav.favorites')}
                      </Link>
                      <Link to="/checkin" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#57534E] dark:text-[#A8A29E] hover:bg-[#F5F4F0] dark:hover:bg-[#2A2A2A]" data-testid="menu-checkin">
                        <DoorOpen className="w-4 h-4" /> Check-in
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to="/mi-cuenta" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#57534E] dark:text-[#A8A29E] hover:bg-[#F5F4F0] dark:hover:bg-[#2A2A2A]" data-testid="menu-mi-cuenta">
                        <User className="w-4 h-4" /> Mi Cuenta
                      </Link>
                      <Link to="/mis-reservas" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#57534E] dark:text-[#A8A29E] hover:bg-[#F5F4F0] dark:hover:bg-[#2A2A2A]" data-testid="menu-mis-reservas">
                        <CalendarDays className="w-4 h-4" /> Mis Reservas
                      </Link>
                      <Link to="/wishlist" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#57534E] dark:text-[#A8A29E] hover:bg-[#F5F4F0] dark:hover:bg-[#2A2A2A]" data-testid="menu-favorites">
                        <Heart className="w-4 h-4" /> Mis Favoritos
                      </Link>
                      <Link to="/proximos-viajes" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#57534E] dark:text-[#A8A29E] hover:bg-[#F5F4F0] dark:hover:bg-[#2A2A2A]" data-testid="menu-proximos-viajes">
                        <Plane className="w-4 h-4" /> Proximos Viajes
                      </Link>
                      <Link to="/checkin" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#57534E] dark:text-[#A8A29E] hover:bg-[#F5F4F0] dark:hover:bg-[#2A2A2A]" data-testid="menu-checkin">
                        <DoorOpen className="w-4 h-4" /> Check In y Check Out
                      </Link>
                      <Link to="/notifications" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#57534E] dark:text-[#A8A29E] hover:bg-[#F5F4F0] dark:hover:bg-[#2A2A2A]" data-testid="menu-notifications">
                        <Bell className="w-4 h-4" /> Notificaciones
                      </Link>
                    </>
                  )}
                  <div className="border-t border-[#E7E5E4] dark:border-[#333] mt-1 pt-1">
                    <button onClick={logout} className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#C36F4D] hover:bg-[#FFF5F0] dark:hover:bg-[#2A2020] w-full" data-testid="menu-logout">
                      <LogOut className="w-4 h-4" /> {t('nav.logout')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Button onClick={login} className="bg-[#264639] hover:bg-[#1C352B]" data-testid="login-btn">
                {t('nav.login')}
              </Button>
            )}
            <ThemeToggle />
          </div>

          <button className="md:hidden p-2 text-[#1C1917] dark:text-[#F5F4F0]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white dark:bg-[#1E1E1E] border-t border-[#E7E5E4] dark:border-[#333] py-4 px-4 space-y-3">
          <Link to="/properties" className="block text-[#57534E] dark:text-[#D6D3D1] hover:text-[#264639] dark:hover:text-[#6CC4A1] py-2" data-testid="mobile-nav-properties">
            {t('nav.properties')}
          </Link>
          {isAuthenticated ? (
            <>
              {isAdmin ? (
                <>
                  <Link to="/dashboard" className="block text-[#57534E] dark:text-[#D6D3D1] hover:text-[#264639] dark:hover:text-[#6CC4A1] py-2">
                    {t('nav.dashboard')}
                  </Link>
                  <Link to="/wishlist" className="block text-[#57534E] dark:text-[#D6D3D1] hover:text-[#264639] dark:hover:text-[#6CC4A1] py-2">
                    {t('nav.favorites')}
                  </Link>
                  <Link to="/checkin" className="block text-[#57534E] dark:text-[#D6D3D1] hover:text-[#264639] dark:hover:text-[#6CC4A1] py-2">
                    Check-in
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/mi-cuenta" className="block text-[#57534E] dark:text-[#D6D3D1] hover:text-[#264639] dark:hover:text-[#6CC4A1] py-2">
                    Mi Cuenta
                  </Link>
                  <Link to="/mis-reservas" className="block text-[#57534E] dark:text-[#D6D3D1] hover:text-[#264639] dark:hover:text-[#6CC4A1] py-2">
                    Mis Reservas
                  </Link>
                  <Link to="/wishlist" className="block text-[#57534E] dark:text-[#D6D3D1] hover:text-[#264639] dark:hover:text-[#6CC4A1] py-2">
                    Mis Favoritos
                  </Link>
                  <Link to="/proximos-viajes" className="block text-[#57534E] dark:text-[#D6D3D1] hover:text-[#264639] dark:hover:text-[#6CC4A1] py-2">
                    Proximos Viajes
                  </Link>
                  <Link to="/checkin" className="block text-[#57534E] dark:text-[#D6D3D1] hover:text-[#264639] dark:hover:text-[#6CC4A1] py-2">
                    Check In y Check Out
                  </Link>
                  <Link to="/notifications" className="block text-[#57534E] dark:text-[#D6D3D1] hover:text-[#264639] dark:hover:text-[#6CC4A1] py-2">
                    Notificaciones
                  </Link>
                </>
              )}
              <button onClick={logout} className="block text-[#C36F4D] py-2 w-full text-left">
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <Button onClick={login} className="w-full bg-[#264639] hover:bg-[#1C352B]">
              {t('nav.login')}
            </Button>
          )}
          <div className="pt-2 border-t border-[#E7E5E4] dark:border-[#333] space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1" data-testid="mobile-lang-selector">
                {LOCALES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLocale(l.code)}
                    className={`px-3 py-1.5 rounded-full text-sm ${locale === l.code ? 'bg-[#264639] text-white' : 'text-[#78716C] dark:text-[#A8A29E]'}`}
                    data-testid={`mobile-lang-${l.code}`}
                  >
                    {l.flag} {l.label}
                  </button>
                ))}
              </div>
              <ThemeToggle />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#78716C] dark:text-[#A8A29E] font-medium">Moneda</span>
              <CurrencySelector className="" />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
